import React, { useContext, useState, useEffect } from 'react';
import { useOrganizations } from '../../contexts/OrganizationsContext';
import AcornMain from '../../components/Acorn/Acorn';
import OrganizationForm from '../../components/Organization/OrganizationForm';
import {
  Users,
  School,
  Settings as SettingsIcon,
  LogOut,
  Search,
  Download,
  UserPlus,
  BarChart3,
  GraduationCap,
  Award,
  Bell,
  Shield,
  Building2,
  UserCog,
  Coins,
  Filter,
  Tag,
  Package,
  Store,
  Save,
  CurrencyIcon,
  UserCheck,
} from 'lucide-react';
import HallPasses from '../HallPasses/HallPasses';  // import HallPasses
import UserManagement from '../Users/UserManagement';
import { UserContext } from '../../contexts/UserContext';
import StoreEditor from '../Store/Store';
import HallPassTypesPage from '../../pages/HallPassTypesPage';
import { useNavigate, useLocation } from 'react-router-dom';
import { Organization } from '../../models/organization';
import AdminToolsPage from '../../pages/AdminToolsPage';

type School = {
  id: string;
  name: string;
  address: string;
  totalStudents: number;
  totalTeachers: number;
  status: 'active' | 'pending' | 'suspended';
  lastSync: string;
};

export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, handleLogout, isImpersonating, impersonatedUser } = useContext(UserContext);
  const { organizations, schools, teachers, students } = useOrganizations();

  const [activeTab, setActiveTab] = useState<'overview' | 'schools' | 'users' | 'store' | 'acorn' | 'hallpasses' | 'hallpassTypes' | 'badges' | 'dev-tools'>('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'pending' | 'suspended' | 'inactive'>('all');
  const [storeSettings, setStoreSettings] = useState({
    pointsName: 'Points',
    enableAuctions: true,
    enablePurchaseLimits: true,
    defaultPurchaseLimit: 5,
    minPointsPerItem: 10,
    maxPointsPerItem: 1000,
    allowTeacherProducts: true,
    requireApproval: true,
    notifyParents: true,
    categories: [
      'Supplies',
      'Passes',
      'Rewards',
      'Special Items'
    ],
    pickupLocations: [
      'Main Office',
      'Library',
      'Cafeteria',
      'Student Store'
    ]
  });

  const handleStoreSettingsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Save store settings
    console.log('Store settings saved:', storeSettings);
  };

  // Removed unused status helpers

  const [showOrganizationForm, setShowOrganizationForm] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Partial<Organization> | null>(null);

  const handleOrganizationSave = 
  () => {};
  
  useEffect(() => {
    if (location.pathname === '/admin/stores') {
      setActiveTab('store');
    } else if (location.pathname === '/admin/hallpass-types') {
      setActiveTab('hallpassTypes');
    } else if (location.pathname === '/admin/badges') {
      setActiveTab('badges');
    }
  }, [location.pathname]);

  return (
    <div className="dashboard-bg-alt">
      {/* Impersonation Banner */}
      {isImpersonating && impersonatedUser && (
        <div className="bg-red-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-center gap-3">
              <UserCheck className="w-5 h-5" />
              <span className="font-medium">
                You are currently impersonating: {impersonatedUser.fullName} ({impersonatedUser.email})
              </span>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-white/60 backdrop-blur-md border-b border-indigo-100/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2">
                <Shield className="w-8 h-8 text-indigo-600" />
                <span className="text-xl font-bold text-gray-800">Admin Dashboard</span>
              </div>
              <nav className="hidden md:flex items-center gap-1">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'overview'
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('schools')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'schools'
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Schools
                </button>
                <button
                  onClick={() => setActiveTab('users')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'users'
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Users
                </button>
                <button
                  onClick={() => setActiveTab('store')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'store'
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Store
                </button>
                <button
                  onClick={() => setActiveTab('acorn')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'acorn'
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Coupons
                </button>
                <button
                  onClick={() => setActiveTab('hallpasses')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'hallpasses'
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Hall Passes
                </button>
                <button
                  onClick={() => { setActiveTab('badges'); navigate('/admin/badges'); }}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'badges'
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Badges
                </button>
                <button
                  onClick={() => { navigate('/admin/tenants'); }}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors text-gray-600 hover:bg-gray-50`}
                >
                  Tenants
                </button>
                <button
                  onClick={() => { navigate('/admin/courses'); }}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors text-gray-600 hover:bg-gray-50`}
                >
                  Courses
                </button>
                <button
                  onClick={() => setActiveTab('dev-tools')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'dev-tools'
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Dev Tools
                </button>
                <button
                  onClick={() => setActiveTab('hallpassTypes')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    (activeTab as string) === 'hallpassTypes'
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Hall Pass Types
                </button>
              </nav>
            </div>
            <div className="flex items-center gap-4">
              <button className="p-2 hover:bg-indigo-50 rounded-lg transition-colors">
                <Bell className="w-5 h-5 text-gray-600" />
              </button>
              <button className="p-2 hover:bg-indigo-50 rounded-lg transition-colors">
                <SettingsIcon className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 hover:bg-indigo-50 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                <img
                  title='Profile photo'
                  src={userProfile?.picture}
                  alt="Admin"
                  width={256}
                  height={256}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-gray-800">{userProfile?.name}</p>
                  <p className="text-xs text-gray-500">{userProfile?.email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="dashboard-card">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <School className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Schools</p>
                    <p className="text-2xl font-bold text-gray-800">{schools.length}</p>
                  </div>
                </div>
              </div>
              <div className="dashboard-card">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-50 rounded-xl">
                    <GraduationCap className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Teachers</p>
                    <p className="text-2xl font-bold text-gray-800">{teachers.length}</p>
                  </div>
                </div>
              </div>
              <div className="dashboard-card">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-50 rounded-xl">
                    <Users className="w-6 h-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Students</p>
                    <p className="text-2xl font-bold text-gray-800">{students.length}</p>
                  </div>
                </div>
              </div>
              <div className="dashboard-card">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-50 rounded-xl">
                    <Coins className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Points Awarded</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {organizations.reduce((acc, org) => acc + (org.pointsAwarded || 0), 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
              <button
                className="p-4 bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 hover:bg-white/80 transition-colors text-center"
                onClick={() => {
                  setSelectedOrganization(null);
                  setShowOrganizationForm(true);
                }}
              >
                <Building2 className="w-6 h-6 text-indigo-500 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-800">Add School</span>
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className="p-4 bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 hover:bg-white/80 transition-colors text-center"
              >
                <UserCog className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-800">Manage Users</span>
              </button>
              <button className="p-4 bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 hover:bg-white/80 transition-colors text-center">
                <BarChart3 className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-800">Reports</span>
              </button>
              <button className="p-4 bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 hover:bg-white/80 transition-colors text-center">
                <SettingsIcon className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-800">Settings</span>
              </button>
              <button className="p-4 bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 hover:bg-white/80 transition-colors text-center"
                onClick={() => setActiveTab('acorn')}
              >
                <CurrencyIcon className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-800">Coupons</span>
              </button>
              <button 
                className="p-4 bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 hover:bg-white/80 transition-colors text-center"
                onClick={() => setActiveTab('dev-tools')}
              >
                <UserCheck className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-800">Impersonate</span>
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="dashboard-card">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <School className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-sm text-gray-800">New school added: Lincoln Middle School</p>
                      <p className="text-xs text-gray-500">2 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <UserPlus className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-sm text-gray-800">New teacher account created</p>
                      <p className="text-xs text-gray-500">4 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Award className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-sm text-gray-800">Achievement system updated</p>
                      <p className="text-xs text-gray-500">1 day ago</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">System Status</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-800">Database</span>
                    </div>
                    <span className="text-sm text-gray-500">Operational</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-800">API</span>
                    </div>
                    <span className="text-sm text-gray-500">Operational</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-800">Authentication</span>
                    </div>
                    <span className="text-sm text-gray-500">Operational</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'users' ? (
          <div className="space-y-6">
            <div className="dashboard-card p-4">
              <UserManagement />
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="dashboard-card">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="flex items-center gap-4 w-full sm:w-auto">
                  <div className="relative flex-1 sm:flex-none">
                    <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder={`Search ${activeTab}...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 bg-white/50 border border-gray-200 rounded-lg w-full sm:w-64 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                    />
                  </div>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value as any)}
                    className="px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  >
                    {activeTab === 'hallpassTypes' ? (
                      <>
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </>
                    ) : (
                      <>
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="suspended">Suspended</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <button className="px-4 py-2 bg-white/50 text-gray-700 rounded-lg hover:bg-white transition-colors flex items-center gap-2 w-full sm:w-auto justify-center">
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                  {(activeTab as string) === 'hallpassTypes' ? (
                    <button
                      className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                    >
                      <Search className="w-4 h-4" />
                      Search
                    </button>
                  ) : (
                    <button
                      className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                      onClick={() => {
                        setSelectedOrganization(null);
                        setShowOrganizationForm(true);
                      }}
                    >
                      <UserPlus className="w-4 h-4" />
                      Add {activeTab.slice(0, -1)}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {activeTab !== 'hallpassTypes' && activeTab !== 'badges' && (
              <div className="dashboard-card overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-800">
                    {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Management
                  </h2>
                </div>
                <div className="divide-y divide-gray-100">
                  {activeTab === 'schools' && schools.map(school => (
                    <div
                      key={school.organizationId}
                      className="p-4 hover:bg-indigo-50/50 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedOrganization(school);
                        setShowOrganizationForm(true);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-blue-50 rounded-lg">
                            <School className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">{school.organizationName}</h3>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="hidden sm:block text-right">
                          <p className="text-sm text-gray-600">{school.totalStudents} Students</p>
                          <p className="text-sm text-gray-600">{school.totalTeachers} Teachers</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  ))}

                  {/* Users managed in dedicated tab; teacher/student inline sections removed */}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'store' && (
          <div className="space-y-6">
            <div className="dashboard-card">
            {import.meta.env.VITE_ACORN_EDITION === 'full' && (
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Store className="w-6 h-6 text-indigo-500" />
                  <h2 className="text-xl font-semibold text-gray-800">Store Settings</h2>
                </div>
                <button
                  onClick={handleStoreSettingsSubmit}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Changes
                </button>
              </div>
            )}
              <form onSubmit={handleStoreSettingsSubmit} className="space-y-8">
              {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-gray-500" />
                    General Settings
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Points Name
                      </label>
                      <input
                        type="text"
                        value={storeSettings.pointsName}
                        onChange={(e) => setStoreSettings(prev => ({
                          ...prev,
                          pointsName: e.target.value
                        }))}
                        className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Default Purchase Limit
                      </label>
                      <input
                        type="number"
                        value={storeSettings.defaultPurchaseLimit}
                        onChange={(e) => setStoreSettings(prev => ({
                          ...prev,
                          defaultPurchaseLimit: parseInt(e.target.value)
                        }))}
                        className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                      />
                    </div>
                  </div>
                </div>
                )}
                {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <Coins className="w-5 h-5 text-gray-500" />
                    Points Settings
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum Points per Item
                      </label>
                      <input
                        type="number"
                        value={storeSettings.minPointsPerItem}
                        onChange={(e) => setStoreSettings(prev => ({
                          ...prev,
                          minPointsPerItem: parseInt(e.target.value)
                        }))}
                        className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Maximum Points per Item
                      </label>
                      <input
                        type="number"
                        value={storeSettings.maxPointsPerItem}
                        onChange={(e) => setStoreSettings(prev => ({
                          ...prev,
                          maxPointsPerItem: parseInt(e.target.value)
                        }))}
                        className="w-full px-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                      />
                    </div>
                  </div>
                </div>
                )}
                {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <Package className="w-5 h-5 text-gray-500" />
                    Features
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={storeSettings.enableAuctions}
                          onChange={(e) => setStoreSettings(prev => ({
                            ...prev,
                            enableAuctions: e.target.checked
                          }))}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">Enable Auctions</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={storeSettings.enablePurchaseLimits}
                          onChange={(e) => setStoreSettings(prev => ({
                            ...prev,
                            enablePurchaseLimits: e.target.checked
                          }))}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">Enable Purchase Limits</span>
                      </label>
                    </div>
                    <div className="space-y-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={storeSettings.allowTeacherProducts}
                          onChange={(e) => setStoreSettings(prev => ({
                            ...prev,
                            allowTeacherProducts: e.target.checked
                          }))}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">Allow Teacher Products</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={storeSettings.requireApproval}
                          onChange={(e) => setStoreSettings(prev => ({
                            ...prev,
                            requireApproval: e.target.checked
                          }))}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">Require Purchase Approval</span>
                      </label>
                    </div>
                  </div>
                </div>
                )}
                {import.meta.env.VITE_ACORN_EDITION === 'full' && (<div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <Filter className="w-5 h-5 text-gray-500" />
                    Categories & Locations
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Product Categories
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {storeSettings.categories.map((category, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-sm font-medium"
                          >
                            {category}
                          </span>
                        ))}
                        <button
                          type="button"
                          className="px-3 py-1 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                        >
                          + Add
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pickup Locations
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {storeSettings.pickupLocations.map((location, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-green-50 text-green-600 rounded-lg text-sm font-medium"
                          >
                            {location}
                          </span>
                        ))}
                        <button
                          type="button"
                          className="px-3 py-1 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                        >
                          + Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>)}
                <StoreEditor />
              </form>
            </div>
          </div>
        )}
        {activeTab === 'acorn' && <AcornMain />}
        {activeTab === 'hallpasses' && (
          <HallPasses />
        )}
        {(activeTab as string) === 'hallpassTypes' && (
          <HallPassTypesPage searchTerm={searchTerm} statusFilter={selectedStatus as 'all' | 'active' | 'inactive'} />
        )}
        {(activeTab === 'badges' || activeTab === 'dev-tools') && <AdminToolsPage initialTool={activeTab === 'dev-tools' ? 'dev-tools' : undefined} />}
      </div>

      {showOrganizationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-11/12 sm:w-3/4 md:w-1/2">
            <OrganizationForm
              organizationType = { 2 }
              newOrganization={selectedOrganization || {}}
              setNewOrganization={setSelectedOrganization}
              isEditMode={!!selectedOrganization?.organizationId}
              onClose={() => setShowOrganizationForm(false)}
              onSave={handleOrganizationSave}
            />
            
          </div>
        </div>
      )}
    </div>
  );
}